// @flow weak

import _ from 'lodash/fp';
import {combineReducers} from 'redux';
import {
  GET_REQUEST, GET_SUCCESS,
  GET_FAILURE, CREATE_REQUEST,
  CREATE_SUCCESS, CREATE_FAILURE,
  UPDATE_REQUEST, UPDATE_SUCCESS,
  UPDATE_FAILURE, ORDER_ROWS_UPDATE_SUCCESS,
  CLEAR, RESET,
  SET_NOTE_ROW, ORDER_ROW_UPDATE_REQUEST,
  ORDER_ROW_UPDATE_FAILURE, ORDER_ROW_UPDATE_SUCCESS,
  SET_ACTIVE_COMMENT, ORDER_COMMENT_CREATE_REQUEST,
  ORDER_COMMENT_CREATE_FAILURE, ORDER_COMMENT_CREATE_SUCCESS,
  ORDER_COMMENT_REMOVE_REQUEST, ORDER_COMMENT_REMOVE_FAILURE,
  ORDER_COMMENT_REMOVE_SUCCESS, ORDER_COMMENT_UPDATE_REQUEST,
  ORDER_COMMENT_UPDATE_FAILURE, ORDER_COMMENT_UPDATE_SUCCESS,
  UPDATE_REPORT_FILTER, UPDATE_USER_ORDER_FILTER,
  SET_ACTIVE_EXTRA_ROW, EXTRA_ROW_CREATE_REQUEST,
  EXTRA_ROW_CREATE_SUCCESS, EXTRA_ROW_CREATE_FAILURE,
  EXTRA_ROW_REMOVE_REQUEST, EXTRA_ROW_REMOVE_FAILURE,
  EXTRA_ROW_REMOVE_SUCCESS, EXTRA_ROW_UPDATE_REQUEST,
  EXTRA_ROW_UPDATE_FAILURE, EXTRA_ROW_UPDATE_SUCCESS,
  ACTIVE_EXTRA_ROW_PRODUCTS_REQUEST, ACTIVE_EXTRA_ROW_PRODUCTS_SUCCESS,
  ACTIVE_EXTRA_ROW_PRODUCTS_FAILURE, ACTIVE_EXTRA_ROW_VARIANTS_REQUEST,
  ACTIVE_EXTRA_ROW_VARIANTS_SUCCESS, ACTIVE_EXTRA_ROW_VARIANTS_FAILURE,
  ORDER_ROWS_CLEAR_REQUEST, ORDER_ROWS_CLEAR_SUCCESS,
  ORDER_ROWS_CLEAR_FAILURE,
} from '../actions/orderView';
import {SET_DIRTY} from '../actions/form';
import {totalPrice} from '../helpers/priceHelpers';
import {productRowsByRoom} from './helpers';
import {groupSlots} from './customerOrderView';

const active = (state = null, action) => {
  switch (action.type) {
    case GET_SUCCESS:
    case CREATE_SUCCESS:
    case UPDATE_SUCCESS:
      return !!action.order ? {
        ...action.order,
        projectId: action.order.apartment.projectId,
      } : null;
    case ORDER_COMMENT_CREATE_SUCCESS: {
      if (!state) {
        return state;
      }
      return {
        ...state,
        comments: [
          ...state.comments,
          action.comment,
        ],
      };
    }
    case ORDER_COMMENT_REMOVE_SUCCESS: {
      if (!state) {
        return state;
      }
      return {
        ...state,
        comments: _.reject({id: action.id}, state.comments),
      };
    }
    case ORDER_COMMENT_UPDATE_SUCCESS: {
      if (!state) {
        return state;
      }
      const idx = _.findIndex({id: action.comment.id}, state.comments);
      return {
        ...state,
        comments: [
          ..._.slice(0, idx, state.comments),
          action.comment,
          ..._.slice(idx + 1, state.comments.length, state.comments),
        ],
      };
    }
    case ORDER_ROWS_UPDATE_SUCCESS:
      if (!state) {
        return state;
      }
      return {
        ...state,
        rows: action.rows,
      };
    case ORDER_ROWS_CLEAR_SUCCESS:
      if (!state) {
        return state;
      }
      return {
        ...state,
        rows: [],
      };
    case GET_REQUEST:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const orderRowsBySlotId = (state = {}, action) => {
  switch (action.type) {
    case GET_SUCCESS:
    case CREATE_SUCCESS:
    case UPDATE_SUCCESS:
      return !!action.order
        ? groupSlots(action.order.rows)
        : {};
    case ORDER_ROW_UPDATE_SUCCESS:
    case ORDER_ROWS_UPDATE_SUCCESS:
      return groupSlots(action.rows);
    case ORDER_ROWS_CLEAR_SUCCESS:
    case GET_REQUEST:
    case CLEAR:
      return {};
    default:
      return state;
  }
};

const rooms = (state = [], action) => {
  switch (action.type) {
    case GET_SUCCESS:
      return !!action.apartmentConfig
        ? action.apartmentConfig.rooms || []
        : [];
    case GET_REQUEST:
    case CLEAR:
      return [];
    default:
      return state;
  }
};

const error = (state = null, action) => {
  switch (action.type) {
    case GET_REQUEST:
    case CREATE_REQUEST:
    case UPDATE_REQUEST:
    case GET_SUCCESS:
    case CREATE_SUCCESS:
    case UPDATE_SUCCESS:
    case EXTRA_ROW_REMOVE_REQUEST:
    case EXTRA_ROW_REMOVE_SUCCESS:
    case ORDER_ROWS_CLEAR_REQUEST:
    case CLEAR:
      return null;
    case GET_FAILURE:
    case CREATE_FAILURE:
    case UPDATE_FAILURE:
    case EXTRA_ROW_REMOVE_FAILURE:
    case ORDER_ROWS_CLEAR_FAILURE:
      return action.error;
    default:
      return state;
  }
};

const isDirty = (state = false, action) => {
  switch (action.type) {
    case SET_DIRTY:
      return action.form === 'order'
        ? action.dirty
        : state;
    case GET_SUCCESS:
    case UPDATE_SUCCESS:
    case CREATE_SUCCESS:
    case RESET:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const isFetching = (state = false, action) => {
  switch (action.type) {
    case GET_REQUEST:
    case CREATE_REQUEST:
    case UPDATE_REQUEST:
    case EXTRA_ROW_REMOVE_REQUEST:
    case ORDER_ROWS_CLEAR_REQUEST:
      return true;
    case GET_SUCCESS:
    case CREATE_SUCCESS:
    case UPDATE_SUCCESS:
    case GET_FAILURE:
    case CREATE_FAILURE:
    case UPDATE_FAILURE:
    case EXTRA_ROW_REMOVE_SUCCESS:
    case EXTRA_ROW_REMOVE_FAILURE:
    case ORDER_ROWS_CLEAR_SUCCESS:
    case ORDER_ROWS_CLEAR_FAILURE:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const groupByProjectId =
  _.groupBy('projectId');

const apartmentsByProjectId = (state = {}, action) => {
  switch (action.type) {
    case GET_SUCCESS:
      return groupByProjectId(action.apartments);
    case CLEAR:
      return {};
    default:
      return state;
  }
};

const mapUniqueProjects =
  _.flow(
    _.map('project'),
    _.uniqBy('id')
  );

const projects = (state = [], action) => {
  switch (action.type) {
    case GET_SUCCESS:
      return mapUniqueProjects(action.apartments);
    case CLEAR:
      return [];
    default:
      return state;
  }
};

const apartments = combineReducers({
  byProjectId: apartmentsByProjectId,
  projects,
});

const users = (state = [], action) => {
  switch (action.type) {
    case GET_SUCCESS:
      return action.users;
    case CLEAR:
      return [];
    default:
      return state;
  }
};

const salesUsers = (state = [], action) => {
  switch (action.type) {
    case GET_SUCCESS:
      return action.salesUsers;
    case CLEAR:
      return [];
    default:
      return state;
  }
};

const groupByProductTypeId =
  _.groupBy('productTypeId');

const productsByProductTypeId = (state = {}, action) => {
  switch (action.type) {
    case GET_SUCCESS:
      return groupByProductTypeId(action.specialProducts);
    case CLEAR:
      return {};
    default:
      return state;
  }
};

const mapUniqueProductTypes =
  _.flow(
    _.map('productType'),
    _.uniqBy('id')
  );

const productTypes = (state = [], action) => {
  switch (action.type) {
    case GET_SUCCESS:
      return mapUniqueProductTypes(action.specialProducts);
    case CLEAR:
      return [];
    default:
      return state;
  }
};

const specialProducts = combineReducers({
  byProductTypeId: productsByProductTypeId,
  productTypes,
});

const defaultCustomRows = {
  list: [],
};

const customRows = (state = defaultCustomRows, action) => {
  switch (action.type) {
    case GET_SUCCESS:
      return {
        ...state,
        list: !!action.order
          ? action.order.customRows
          : [],
      };
    case CLEAR:
      return defaultCustomRows;
    default:
      return state;
  }
};

const defaultRowsState = {
  rooms: [],
  rows: [],
  extraRows: [],
  allRowsByRoom: [],
};

const rows = (state = defaultRowsState, action) => {
  switch (action.type) {
    case GET_SUCCESS:
      return !!action.order && !!action.apartmentConfig
        ? {
          ...state,
          rooms: action.apartmentConfig.rooms,
          rows: action.order.rows,
          extraRows: action.order.extraRows,
          allRowsByRoom: productRowsByRoom(action.apartmentConfig.rooms, action.order.rows, action.order.extraRows),
        } : defaultRowsState;
    case CREATE_SUCCESS:
    case UPDATE_SUCCESS: {
      return {
        ...state,
        rows: action.order.rows,
        extraRows: action.order.extraRows,
        allRowsByRoom: productRowsByRoom(state.rooms, action.order.rows, action.order.extraRows),
      };
    }
    case ORDER_ROW_UPDATE_SUCCESS:
    case ORDER_ROWS_UPDATE_SUCCESS:
      return {
        ...state,
        rows: action.rows,
        allRowsByRoom: productRowsByRoom(state.rooms, action.rows, state.extraRows),
      };
    case EXTRA_ROW_CREATE_SUCCESS: {
      const extraRows = [...state.extraRows, action.row];
      return {
        ...state,
        extraRows,
        allRowsByRoom: productRowsByRoom(state.rooms, state.rows, extraRows),
      };
    }
    case EXTRA_ROW_UPDATE_SUCCESS: {
      const extraRows = _.filter(r => r.id !== action.row.id, state.extraRows)
        .concat([action.row]);
      return {
        ...state,
        extraRows,
        allRowsByRoom: productRowsByRoom(state.rooms, state.rows, extraRows),
      };
    }
    case EXTRA_ROW_REMOVE_SUCCESS:
      const extraRows = _.filter(r => r.id !== action.id, state.extraRows);
      return {
        ...state,
        extraRows,
        allRowsByRoom: productRowsByRoom(state.rooms, state.rows, extraRows),
      };
    case ORDER_ROWS_CLEAR_SUCCESS:
      return {
        ...state,
        extraRows: defaultRowsState.extraRows,
        rows: defaultRowsState.rows,
        allRowsByRoom: defaultRowsState.allRowsByRoom,
      };
    case GET_REQUEST:
    case CLEAR:
      return defaultRowsState;
    default:
      return state;
  }
};

const noteRowIsExtra = (state = false, action) => {
  switch (action.type) {
    case SET_NOTE_ROW:
      return action.isExtra;
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const noteRowActive = (state = null, action) => {
  switch (action.type) {
    case SET_NOTE_ROW:
      return action.row;
    case ORDER_ROW_UPDATE_SUCCESS:
    case EXTRA_ROW_UPDATE_SUCCESS:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const noteRowError = (state = null, action) => {
  switch (action.type) {
    case ORDER_ROW_UPDATE_FAILURE:
    case EXTRA_ROW_UPDATE_FAILURE:
      return action.error;
    case ORDER_ROW_UPDATE_SUCCESS:
    case EXTRA_ROW_UPDATE_SUCCESS:
    case SET_NOTE_ROW:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const noteRowIsFetching = (state = false, action) => {
  switch (action.type) {
    case ORDER_ROW_UPDATE_REQUEST:
    case EXTRA_ROW_UPDATE_REQUEST:
      return true;
    case ORDER_ROW_UPDATE_SUCCESS:
    case ORDER_ROW_UPDATE_FAILURE:
    case EXTRA_ROW_UPDATE_SUCCESS:
    case EXTRA_ROW_UPDATE_FAILURE:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const noteRow = combineReducers({
  isExtra: noteRowIsExtra,
  active: noteRowActive,
  error: noteRowError,
  isFetching: noteRowIsFetching,
});

const orderCommentActive = (state = null, action) => {
  switch (action.type) {
    case SET_ACTIVE_COMMENT:
      return action.comment;
    case ORDER_COMMENT_CREATE_SUCCESS:
    case ORDER_COMMENT_REMOVE_SUCCESS:
    case ORDER_COMMENT_UPDATE_SUCCESS:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const orderCommentError = (state = null, action) => {
  switch (action.type) {
    case ORDER_COMMENT_CREATE_FAILURE:
    case ORDER_COMMENT_REMOVE_FAILURE:
    case ORDER_COMMENT_UPDATE_FAILURE:
      return action.error;
    case ORDER_COMMENT_CREATE_SUCCESS:
    case ORDER_COMMENT_REMOVE_SUCCESS:
    case ORDER_COMMENT_UPDATE_SUCCESS:
    case SET_ACTIVE_COMMENT:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const orderCommentIsFetching = (state = false, action) => {
  switch (action.type) {
    case ORDER_COMMENT_CREATE_REQUEST:
    case ORDER_COMMENT_REMOVE_REQUEST:
    case ORDER_COMMENT_UPDATE_REQUEST:
      return true;
    case ORDER_COMMENT_CREATE_SUCCESS:
    case ORDER_COMMENT_CREATE_FAILURE:
    case ORDER_COMMENT_REMOVE_SUCCESS:
    case ORDER_COMMENT_REMOVE_FAILURE:
    case ORDER_COMMENT_UPDATE_SUCCESS:
    case ORDER_COMMENT_UPDATE_FAILURE:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const orderCommentRoomTypes = (state = [], action) => {
  switch (action.type) {
    case GET_SUCCESS:
      return action.roomTypes;
    case CLEAR:
      return [];
    default:
      return state;
  }
};

const orderCommentWorkerTypes = (state = [], action) => {
  switch (action.type) {
    case GET_SUCCESS:
      return action.workerTypes;
    case CLEAR:
      return [];
    default:
      return state;
  }
};

const orderComment = combineReducers({
  active: orderCommentActive,
  error: orderCommentError,
  isFetching: orderCommentIsFetching,
  roomTypes: orderCommentRoomTypes,
  workerTypes: orderCommentWorkerTypes,
});

const suppliers = (state = {}, action) => {
  switch (action.type) {
    case GET_SUCCESS:
    case CREATE_SUCCESS:
    case UPDATE_SUCCESS:
    case ORDER_ROW_UPDATE_SUCCESS:
    case ORDER_ROWS_UPDATE_SUCCESS:
    case ORDER_ROWS_CLEAR_SUCCESS:
    case EXTRA_ROW_REMOVE_SUCCESS:
    case EXTRA_ROW_UPDATE_SUCCESS:
    case EXTRA_ROW_CREATE_SUCCESS:
      return _.flow(
        _.filter(x => !!x && !!x.id),
        _.map(x => x.supplier),
        _.filter(x => !!x && !!x.id),
        _.uniqBy('id'),
        _.sortBy([x => x.name])
      )([
        ..._.map(x => x.product, state.rows.rows),
        ..._.map(x => x.product, state.rows.extraRows),
        ...state.customRows.list,
      ]);
    case CLEAR:
      return [];
    default:
      return state.suppliers;
  }
};

const defaultReportFilterState = {
  includePlans: false,
  supplierId: null,
};

const reportFilter = (state = defaultReportFilterState, action) => {
  switch (action.type) {
    case UPDATE_REPORT_FILTER:
      return {
        ...state,
        includePlans: !!action.includePlans,
        supplierId: action.supplierId,
      };
    case CLEAR:
      return defaultReportFilterState;
    default:
      return state;
  }
};

const defaultUserOrderFilterState = {
  includePlans: false,
};

const userOrderFilter = (state = defaultUserOrderFilterState, action) => {
  switch (action.type) {
    case UPDATE_USER_ORDER_FILTER:
      return {
        ...state,
        includePlans: !!action.includePlans,
      };
    case CLEAR:
      return defaultUserOrderFilterState;
    default:
      return state;
  }
};

const configHasRequiredOrderRows = (config, rows) => {
  const {
    id,
    productVariant,
    standardProducts,
  } = config;
  const requiredProductTypes = _.intersection(
    _.map('productTypeId', standardProducts),
    _.flow(
      _.map(x => x.productTypeId),
      _.uniq
    )(productVariant.related));
  if (!requiredProductTypes.length) {
    return true;
  }
  const childRows = _.filter({parentConfigId: id}, rows);
  const childRowsHasProductType = type => _.some({productTypeId: type}, childRows);
  const hasRequiredRows = _.all(childRowsHasProductType, requiredProductTypes);

  return hasRequiredRows;
};

const checkCompleteAdvancedRows = (slots, rows) => {
  const advancedRows = _.filter('advanced', rows);
  const allActiveConfigs = _.flow(
    _.map(r => ({
      configId: r.configId,
      slot: _.find({id: r.productSlotId}, slots),
    })),
    _.filter(r => !!r.slot),
    _.map(r => _.find({id: r.configId}, r.slot.config)),
  )(advancedRows);

  return _.all(c => configHasRequiredOrderRows(c, rows), allActiveConfigs);
};

export const calculateOrderCompleteState = (orderRowsBySlotId, rooms, orderRows) => {
  let complete = false;
  if (!!orderRowsBySlotId && !!rooms) {
    const productSlots = _.flatMap('productSlots', rooms);
    const slotsAreComplete = !_.some(i => (
      ((!!i.defaultProductId && !i.advanced) || (!!i.standardProductId && !!i.advanced)) &&
      !orderRowsBySlotId[`${i.id}__${!!i.advanced ? i.productTypeId : ''}`]
    ), productSlots);
    const advancedOrderRowsAreComplete = checkCompleteAdvancedRows(productSlots, orderRows);
    complete = slotsAreComplete && advancedOrderRowsAreComplete;
  }
  return complete;
};

const orderComplete = (state, action) => {
  switch (action.type) {
    case GET_SUCCESS:
    case UPDATE_SUCCESS:
    case CREATE_SUCCESS:
    case ORDER_ROWS_UPDATE_SUCCESS:
    case ORDER_ROW_UPDATE_SUCCESS:
    case ORDER_ROWS_CLEAR_SUCCESS:
      return calculateOrderCompleteState(state.orderRowsBySlotId, state.rooms, state.active ? state.active.rows : []);
    case CLEAR:
      return false;
    default:
      return state.orderComplete;
  }
};

const activeExtraRow = (state = null, action) => {
  switch (action.type) {
    case SET_ACTIVE_EXTRA_ROW:
      return action.row;
    case EXTRA_ROW_CREATE_SUCCESS:
    case EXTRA_ROW_UPDATE_SUCCESS:
    case CLEAR:
      return null;
    default:
      return state;
  }
};

const extraRowError = (state = null, action) => {
  switch (action.type) {
    case EXTRA_ROW_CREATE_SUCCESS:
    case SET_ACTIVE_EXTRA_ROW:
    case CLEAR:
      return null;
    case EXTRA_ROW_CREATE_FAILURE:
    case EXTRA_ROW_UPDATE_FAILURE:
      return action.error;
    default:
      return state;
  }
};

const extraRowIsFetching = (state = false, action) => {
  switch (action.type) {
    case EXTRA_ROW_CREATE_REQUEST:
    case EXTRA_ROW_UPDATE_REQUEST:
      return true;
    case EXTRA_ROW_CREATE_SUCCESS:
    case EXTRA_ROW_CREATE_FAILURE:
    case EXTRA_ROW_UPDATE_SUCCESS:
    case EXTRA_ROW_UPDATE_FAILURE:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const extraRow = combineReducers({
  error: extraRowError,
  isFetching: extraRowIsFetching,
  active: activeExtraRow,
});


const activeExtraRowProductsIsFetching = (state = false, action) => {
  switch (action.type) {
    case ACTIVE_EXTRA_ROW_PRODUCTS_REQUEST:
      return true;
    case ACTIVE_EXTRA_ROW_PRODUCTS_SUCCESS:
    case ACTIVE_EXTRA_ROW_PRODUCTS_FAILURE:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const activeExtraRowProductList = (state = [], action) => {
  switch (action.type) {
    case ACTIVE_EXTRA_ROW_PRODUCTS_SUCCESS:
      return action.products;
    case CLEAR:
      return [];
    default:
      return state;
  }
};

const activeExtraRowProducts = combineReducers({
  isFetching: activeExtraRowProductsIsFetching,
  list: activeExtraRowProductList,
});

const activeExtraRowVariantsIsFetching = (state = false, action) => {
  switch (action.type) {
    case ACTIVE_EXTRA_ROW_VARIANTS_REQUEST:
      return true;
    case ACTIVE_EXTRA_ROW_VARIANTS_SUCCESS:
    case ACTIVE_EXTRA_ROW_VARIANTS_FAILURE:
    case CLEAR:
      return false;
    default:
      return state;
  }
};

const activeExtraRowVariantList = (state = [], action) => {
  switch (action.type) {
    case ACTIVE_EXTRA_ROW_VARIANTS_SUCCESS:
      return action.variants;
    case CLEAR:
      return [];
    default:
      return state;
  }
};

const activeExtraRowVariants = combineReducers({
  isFetching: activeExtraRowVariantsIsFetching,
  list: activeExtraRowVariantList,
});

const calculateOrderTotalPrice = state => {
  if (!state.active) {
    return 0;
  }

  return totalPrice({
    ...state.active,
    rows: state.rows.rows,
    extraRows: state.rows.extraRows,
    customRows: state.customRows.list,
  });
};

const orderTotalPrice = (state, action) => {
  switch (action.type) {
    case GET_SUCCESS:
    case UPDATE_SUCCESS:
    case CREATE_SUCCESS:
    case ORDER_ROWS_UPDATE_SUCCESS:
    case ORDER_ROW_UPDATE_SUCCESS:
    case EXTRA_ROW_CREATE_SUCCESS:
    case EXTRA_ROW_UPDATE_SUCCESS:
    case EXTRA_ROW_REMOVE_SUCCESS:
    case ORDER_ROWS_CLEAR_SUCCESS:
      return calculateOrderTotalPrice(state);
    case CLEAR:
      return 0;
    default:
      return state.orderTotalPrice;
  }
};

const reducers = combineReducers({
  active,
  orderRowsBySlotId,
  rooms,
  error,
  isDirty,
  isFetching,
  specialProducts,
  apartments,
  users,
  salesUsers,
  customRows,
  orderComplete: (s = false) => s,
  orderTotalPrice: (s = 0) => s,
  rows,
  extraRow,
  activeExtraRowProducts,
  activeExtraRowVariants,
  noteRow,
  orderComment,
  suppliers: (s = []) => s,
  reportFilter,
  userOrderFilter,
});

const orderView = (state, action) => {
  const newState = reducers(state, action);
  return {
    ...newState,
    orderComplete: orderComplete(newState, action),
    orderTotalPrice: orderTotalPrice(newState, action),
    suppliers: suppliers(newState, action),
  };
};

export default orderView;



// WEBPACK FOOTER //
// ./src/reducers/orderView.js